import React, { useCallback, useMemo } from 'react';
import { message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import Papa from 'papaparse';

import { RcFile, UploadProps } from 'antd/lib/upload/interface';
import { useTranslation } from 'react-i18next';
import * as S from './styles';

export type PapaResultProps = Papa.ParseResult<{ [key: string]: string }>;

interface IFileUploaderProps extends UploadProps {
  onUpload: (
    values:
      | {
          [key: string]: string;
        }[]
      | undefined,
  ) => void;
  uploadColumns: { title: string; dataIndex: string }[] | undefined;
  filesListState: any;
  onRemove?: () => void;
  maxCount?: number;
}
const FileUploader: React.FC<IFileUploaderProps> = ({
  onUpload,
  onRemove,
  uploadColumns,
  filesListState,
  maxCount = 1,
  ...props
}) => {
  const { t } = useTranslation(['file-uploader', 'device-actions-modal']);
  const [uploadFiles, setUploadFiles] = filesListState;

  const handleUpload = useCallback(
    (result: PapaResultProps | undefined) => {
      if (result) {
        try {
          const csvHeaderReference = JSON.stringify(
            uploadColumns?.map((item: any) => item.title),
          );

          const headerInCsv = JSON.stringify(result.meta.fields);
          if (csvHeaderReference === headerInCsv) {
            const devices = result.data.map(
              (device: { [key: string]: string }) => {
                const rowObject = {};
                uploadColumns?.forEach((header: any) => {
                  // @ts-ignore
                  rowObject[header.dataIndex] = device[header.title];
                });
                return rowObject;
              },
            );
            onUpload(devices);
          } else {
            message.error(t('device-actions-modal:invalid-header'));
          }
        } catch (err: any) {
          message.error(
            t('device-actions-modal:invalid-data', { register: err?.message }),
          );
          onUpload(undefined);
        }
      } else {
        onUpload(undefined);
      }
    },
    [onUpload, t, uploadColumns],
  );

  const convertFileInObj = useCallback(
    (file: RcFile) => {
      Papa.parse(file, {
        skipEmptyLines: 'greedy',
        header: true,
        complete: (result: PapaResultProps) => handleUpload(result),
      });
    },
    [handleUpload],
  );

  const uploaderProps = useMemo(
    () => ({
      name: 'file',
      multiple: false,
      accept: '.csv',
      fileList: uploadFiles,
      maxCount,
      beforeUpload: (file: RcFile) => {
        convertFileInObj(file);
        setUploadFiles([file]);
        return false;
      },
      onRemove: () => onRemove?.() ?? setUploadFiles(undefined),
      ...props,
    }),
    [convertFileInObj, maxCount, onRemove, props, uploadFiles, setUploadFiles],
  );

  return (
    <S.UploadDragger {...uploaderProps}>
      <S.DraggerContent>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{t('uploader-title')}</p>
        <p className="ant-upload-hint">{t('uploader-subtitle')}</p>
      </S.DraggerContent>
    </S.UploadDragger>
  );
};

export default FileUploader;
